import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {ListResult, CleanItem, CampaignCleanItem} from 'utils/types';
import {
  withCampaignIdURLParams,
  WithCampaignIdURLParams,
  withFilterURLParams,
  WithFilterURLParams,
  withOrderingURLParams,
  WithOrderingURLParams,
  withPaginationURLParams,
  WithPaginationURLParams,
  withSearchURLParams,
  WithSearchURLParams,
} from 'utils/urlParamHelpers';

export interface FormRow {
  errors: Record<string, Array<string> | string>;
  state: Record<string, Record<keyof CleanItem, string | number>>;
  isSaving: boolean;
}

export interface IState {
  cleanItems: ListResult<CleanItem>;
  campaignCleanItems: ListResult<CampaignCleanItem>;
  formRows: Record<string, FormRow>;
}

type CleanItemListParams = WithSearchURLParams<WithFilterURLParams<WithOrderingURLParams<WithPaginationURLParams>>>;
const serializeCleanItemListParams = withSearchURLParams(
  withFilterURLParams(withOrderingURLParams(withPaginationURLParams())),
);
type CleanItemListExportParams = WithSearchURLParams<WithFilterURLParams<WithOrderingURLParams>>;
const serializeCleanItemListExportParams = withSearchURLParams(withFilterURLParams(withOrderingURLParams()));
type CampaignCleanItemListParams = WithSearchURLParams<
  WithFilterURLParams<WithCampaignIdURLParams<WithOrderingURLParams<WithPaginationURLParams>>>
>;
const serializeCampaignCleanItemListParams = withSearchURLParams(
  withFilterURLParams(withCampaignIdURLParams(withOrderingURLParams(withPaginationURLParams()))),
);

export type CleanItemCreatePayload = {
  meta?: {rowId: string};
  upc: string;
  source_description?: string;
  clean_description?: string;
  category?: string;
  brand?: number | null;
};
export type CleanItemUpdatePayload = {
  meta: {rowId: string};
  id: number | string;
  clean_description?: string;
  category?: string;
  brand?: number | null;
};

export type CleanItemBulkUpdatePayload = {
  clean_item_ids: Array<string>;
  category?: string;
  brand_id?: number | null;
};

export const cleanItemEndpoints = {
  getCleanItems: createAsyncThunk('cleanItem/getCleanItems', async (payload: CleanItemListParams) => {
    const params = new URLSearchParams();
    serializeCleanItemListParams(params, payload);
    const response = await ApiService.get(`/clean_items/?${params.toString()}`);
    return response.data;
  }),
  getCampaignCleanItems: createAsyncThunk(
    'cleanItem/getCampaignCleanItems',
    async (payload: CampaignCleanItemListParams) => {
      const params = new URLSearchParams();
      serializeCampaignCleanItemListParams(params, payload);
      const response = await ApiService.get(`/campaign_clean_items/?${params.toString()}`);
      return response.data;
    },
  ),
  createCleanItem: createAsyncThunk(
    'cleanItem/createCleanItem',
    async (payload: CleanItemCreatePayload, {rejectWithValue}) => {
      const {meta, ...data} = payload;
      try {
        const response = await ApiService.post('/clean_items/', data);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  updateCleanItem: createAsyncThunk(
    'cleanItem/updateCleanItem',
    async (payload: CleanItemUpdatePayload, {rejectWithValue}) => {
      const {meta, ...data} = payload;
      try {
        const response = await ApiService.patch(`/clean_items/${data.id}/`, data);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  bulkUpdateCleanItems: createAsyncThunk(
    'cleanItem/bulkUpdateCleanItems',
    async (payload: CleanItemBulkUpdatePayload, {rejectWithValue}) => {
      try {
        const response = await ApiService.post('/clean_items/bulk_update/', payload);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  exportCleanItems: createAsyncThunk('cleanItem/exportCleanItems', async (payload: CleanItemListExportParams) => {
    const params = new URLSearchParams();
    serializeCleanItemListExportParams(params, payload);
    const response = await ApiService.get<Blob>(`/clean_items/export/?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  }),
};

const getDefaultFormRow = (): FormRow => {
  return {
    errors: {},
    state: {},
    isSaving: false,
  };
};

// These reducers mutate the Immer draft in place rather than returning a new state
// object assembled from draft values. Spreading a draft copies its nested values as
// drafts, and Immer only finalizes string-keyed properties when it walks a returned
// plain object -- the symbol-keyed stale result that addResultCases stores on a
// ListResult is skipped, so the draft under it survives into the store and becomes a
// revoked proxy once the reducer's Immer scope ends. The next dispatch that reads it
// then throws "illegal operation attempted on a revoked proxy".
const createUpdateCleanItemPending = (state, action) => {
  const {meta} = action.meta.arg;
  if (!meta) return;
  const {rowId} = meta;
  if (!state.formRows[rowId]) {
    state.formRows[rowId] = getDefaultFormRow();
  }
  state.formRows[rowId].isSaving = true;
};

const createUpdateCleanItemRejected = (state, action) => {
  const {meta} = action.meta.arg;
  if (!meta) return;
  const {rowId} = meta;
  const errors = (action.payload || {}) as Record<string, Array<string> | string>;
  if (!state.formRows[rowId]) {
    state.formRows[rowId] = getDefaultFormRow();
  }
  state.formRows[rowId].errors = errors;
  state.formRows[rowId].isSaving = false;
};

const createUpdateCleanItemFulfilled = (state, action) => {
  // Clear form row on successful save
  const {meta} = action.meta.arg;
  if (!meta) return;
  const {rowId} = meta;
  delete state.formRows[rowId];

  // Update the item in campaignCleanItems or cleanItems with the server response
  const updatedItem = action.payload;

  // Update campaignCleanItems if it has results
  const {campaignCleanItems, cleanItems} = state;
  if (campaignCleanItems.as === 'success' && campaignCleanItems.results) {
    campaignCleanItems.results.forEach(item => {
      // Match by UPC for campaign clean items
      if (item.upc === rowId) {
        Object.assign(item, updatedItem, {clean_item_id: updatedItem.id});
      }
    });
  }

  // Update cleanItems if it has results
  if (cleanItems.as === 'success' && cleanItems.results) {
    cleanItems.results.forEach(item => {
      // Match by ID or UPC for clean items
      if (item.id === updatedItem.id || item.upc === rowId) {
        Object.assign(item, updatedItem);
      }
    });
  }
};

export const cleanItemSlice = createSlice({
  name: 'cleanItem',
  initialState: {
    cleanItems: {as: 'idle'},
    campaignCleanItems: {as: 'idle'},
    formRows: {},
  } as IState,
  reducers: {
    setFormValue(state, action) {
      const {rowId, field, value} = action.payload;
      state.formRows[rowId] = state.formRows[rowId] || getDefaultFormRow();
      state.formRows[rowId].state[field] = value;
    },
    setFormErrors(state, action) {
      const {rowId, field, errors} = action.payload;
      state.formRows[rowId] = state.formRows[rowId] || getDefaultFormRow();
      state.formRows[rowId].errors[field] = errors;
    },
    clearFormErrors(state, action) {
      const {rowId} = action.payload;
      if (state.formRows[rowId]) {
        state.formRows[rowId].errors = {};
      }
    },
    clearFormRow(state, action) {
      const {rowId} = action.payload;
      delete state.formRows[rowId];
    },
    clearAllFormErrors(state) {
      Object.values(state.formRows).forEach(row => {
        row.errors = {};
      });
    },
    clearAllFormRows(state) {
      state.formRows = {};
    },
  },
  extraReducers: builder => {
    addResultCases(builder, cleanItemEndpoints.getCleanItems, 'cleanItems');
    addResultCases(builder, cleanItemEndpoints.getCampaignCleanItems, 'campaignCleanItems');
    builder.addCase(cleanItemEndpoints.updateCleanItem.pending, (state, action) => {
      return createUpdateCleanItemPending(state, action);
    });
    builder.addCase(cleanItemEndpoints.updateCleanItem.rejected, (state, action) => {
      return createUpdateCleanItemRejected(state, action);
    });
    builder.addCase(cleanItemEndpoints.updateCleanItem.fulfilled, (state, action) => {
      return createUpdateCleanItemFulfilled(state, action);
    });
    builder.addCase(cleanItemEndpoints.createCleanItem.pending, (state, action) => {
      return createUpdateCleanItemPending(state, action);
    });
    builder.addCase(cleanItemEndpoints.createCleanItem.rejected, (state, action) => {
      return createUpdateCleanItemRejected(state, action);
    });
    builder.addCase(cleanItemEndpoints.createCleanItem.fulfilled, (state, action) => {
      return createUpdateCleanItemFulfilled(state, action);
    });
  },
});

export const cleanItemActions = cleanItemSlice.actions;

export type CleanItemSlice = {
  [cleanItemSlice.name]: ReturnType<(typeof cleanItemSlice)['reducer']>;
};
